import { FAKE_SOLD_GROUP_ID } from 'src/components/Listings/constants';
import { ListingWithEvent } from 'src/tables/ListingTable/ListingTable.types';
import { sortSeating } from 'src/utils/tableUtils';
import { addMoneySameCurrency } from 'src/utils/ticketMetricUtils';
import {
  ActionOutboxEntityType,
  Listing,
  ListingGroup,
  ListingStatus,
  Marketplace,
  UiMoney,
} from 'src/WebApiController';

export const createAggregatedListing = (
  listings: ListingWithEvent[] | null | undefined
): ListingWithEvent | null => {
  const firstListingWithEvent = listings?.[0];
  const listing = firstListingWithEvent?.listing;

  if (!listings || !firstListingWithEvent || !listing) {
    return null;
  }

  const allListings: Listing[] = listings
    .map((l) => l.listing)
    .flat()
    .filter((l): l is Listing => !!l && l.isFull)
    .toSorted((a, b) => sortSeating(a.seating, b.seating));

  if (!allListings.length) {
    return null;
  }

  const aggregatedListing = {
    id: Math.abs(listing.id) * -1,
    insertBy: '',
    rowVer: 0,
    ltGrpId: null,
    ltGrpParentId: null,
    ltGrpPrior: -1,
    ltGrpActive: false,
    poBys: [],
    isLtGrp: false,
    isFull: false,
    entityType: ActionOutboxEntityType.Listing,
    isDeleted: false,
    isUndeliv: false,
    mkp: Marketplace.StubHub,
    idOnMkp: listing.idOnMkp,
    posEvId: listing.posEvId,
    viagVirtId: listing.viagVirtId,
    viagEvId: listing.viagEvId,
    viagEvMapId: listing.viagEvMapId,
    ticketCnt: 0,
    unsoldQty: 0,
    soldQty: 0,
    availQty: 0,
    maxDispQty: 0,
    shownQuantity: 0,
    wastedQty: 0,
    mkpListings: [],
    isSeatSaver: false,
    isNoFulfill: false,
    seating: listing.seating,
    splitType: listing.splitType,
    hideSeats: false,
    tags: [],
    seatTraits: [],
    delivType: listing.delivType,
    preDelivState: listing.preDelivState,
    unitCst: null,
    faceValue: null,
    listPrice: listing.listPrice,
    allInPrice: listing.allInPrice,
    actions: [],
    status: ListingStatus.FullySold,
    currency: listing.currency,
    pricerId: '',
    isCurUserPo: listing.isCurUserPo,
    privNotes: '',
    poId: listing.poId,
    poCreatedBys: [],
  } as unknown as Listing;

  allListings.forEach((listing) => {
    aggregatedListing.ticketCnt += listing.ticketCnt;
    aggregatedListing.unsoldQty += listing.unsoldQty;
    aggregatedListing.availQty += listing.availQty;
    aggregatedListing.maxDispQty += listing.maxDispQty;
    aggregatedListing.shownQuantity =
      (aggregatedListing.shownQuantity ?? 0) +
      (listing.manMaxDispQty ?? listing.maxDispQty);
    aggregatedListing.soldQty =
      (aggregatedListing.soldQty ?? 0) + (listing.soldQty ?? 0);
    aggregatedListing.wastedQty =
      (aggregatedListing.wastedQty ?? 0) + (listing.wastedQty ?? 0);
  });

  const currency = allListings[0].unitCst?.currency;
  const allListingsHaveSameCurrency = allListings.every(
    (l) => l.unitCst?.currency === currency || l.unitCst?.currency == null
  );

  if (allListingsHaveSameCurrency) {
    let totalCost: number | null = 0;
    let totalWastedCost: number | null = 0;
    let totalUnsoldCost: number | null = 0;
    let totalSoldCost: number | null = 0;
    let totalUnsoldProceeds: number | null = 0;
    let totalFaceValue: number | null = 0;
    let totalQty = 0;

    for (let i = 0; i < allListings.length; i++) {
      totalCost = addMoneySameCurrency(
        (allListings[i].unitCst?.amt ?? 0) * allListings[i].ticketCnt,
        totalCost
      );
      totalWastedCost = addMoneySameCurrency(
        (allListings[i].unitCst?.amt ?? allListings[i].faceValue?.amt ?? 0) *
          (allListings[i].wastedQty ?? 0),
        totalWastedCost
      );
      totalUnsoldCost = addMoneySameCurrency(
        (allListings[i].unitCst?.amt ?? allListings[i].faceValue?.amt ?? 0) *
          allListings[i].availQty,
        totalUnsoldCost
      );
      totalSoldCost = addMoneySameCurrency(
        (allListings[i].unitCst?.amt ?? allListings[i].faceValue?.amt ?? 0) *
          (allListings[i].ticketCnt - allListings[i].availQty),
        totalSoldCost
      );
      totalFaceValue = addMoneySameCurrency(
        (allListings[i].faceValue?.amt ?? 0) * allListings[i].ticketCnt,
        totalFaceValue
      );

      totalUnsoldProceeds = addMoneySameCurrency(
        allListings[i].listPrice * allListings[i].availQty,
        totalUnsoldProceeds
      );

      totalQty += allListings[i].ticketCnt;
    }

    aggregatedListing.unitCst =
      totalCost != null && totalQty > 0
        ? ({
            amt: totalCost / totalQty,
            currency: currency,
          } as UiMoney)
        : null;

    aggregatedListing.wastedCost =
      totalWastedCost != null
        ? ({
            amt: totalWastedCost,
            currency: currency,
          } as UiMoney)
        : null;

    aggregatedListing.unsoldCost =
      totalUnsoldCost != null
        ? ({
            amt: totalUnsoldCost,
            currency: currency,
          } as UiMoney)
        : null;

    aggregatedListing.soldCost =
      totalSoldCost != null
        ? ({
            amt: totalSoldCost,
            currency: currency,
          } as UiMoney)
        : null;

    aggregatedListing.unsoldProceeds = totalUnsoldProceeds;

    aggregatedListing.faceValue =
      totalFaceValue != null && totalQty > 0
        ? ({
            amt: totalFaceValue / totalQty,
            currency: currency,
          } as UiMoney)
        : null;
  }

  return {
    event: firstListingWithEvent.event,
    listing: aggregatedListing,
  };
};

export const createFakeListingGroup = (
  listings: ListingWithEvent[] | undefined,
  groupName: string
): ListingWithEvent | null => {
  const firstListingWithEvent = listings?.[0];

  const aggregatedListing = createAggregatedListing(listings)?.listing;
  if (!aggregatedListing) {
    return null;
  }

  const allListings: Listing[] = listings!
    .map((l) => l.listing)
    .flat()
    .filter((l): l is Listing => !!l)
    .toSorted((a, b) => sortSeating(a.seating, b.seating));

  const listingGroup = {
    ...aggregatedListing,

    ltGrpId: FAKE_SOLD_GROUP_ID,
    isLtGrp: true,
    groupItems: allListings,
    groupName,
  } as unknown as ListingGroup;

  return {
    isLtGrp: true,
    event: firstListingWithEvent!.event,
    listing: listingGroup,
  };
};
